import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import './LoginPage.css';

function LoginPage() {
  const navigate = useNavigate();
  const { login, deviceId } = useAuth();
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [revokedInfo, setRevokedInfo] = useState(null);

  useEffect(() => {
    document.body.style.background = '#091520';
    return () => { document.body.style.background = ''; };
  }, []);

  useEffect(() => {
    const revokedStr = localStorage.getItem('sessionRevokedByDevice');
    if (revokedStr) {
      try { setRevokedInfo(JSON.parse(revokedStr)); } catch (_) {}
      localStorage.removeItem('sessionRevokedByDevice');
    }
  }, []);

  const doLogin = async (force = false) => {
    setError('');
    setIsSubmitting(true);
    const body = { username, password, device_id: deviceId };
    if (force) body.force = true;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const data = await response.json();
        await login(data.access_token);
        navigate({ to: '/select-service' });
        return;
      }
      let errorData;
      try {
        errorData = await response.json();
      } catch (_) {
        setError(`${t('login.error_generic')} (HTTP ${response.status})`);
        return;
      }
      if (response.status === 409 && errorData.warning === 'device_limit_reached') {
        await doLogin(true);
        return;
      }
      setError(errorData.message || t('login.error_credentials'));
    } catch (err) {
      setError(t('login.error_generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <img src="/logo_bluenetfish.png" alt="Bluenetfish" className="login-card-logo" />

        <div className="login-card-header">
          <p>{t('login.subtitle')}</p>
          <h2>{t('login.title')}</h2>
        </div>

        {revokedInfo && (
          <div className="login-revoked">
            <span>{revokedInfo.message}</span>
            <button onClick={() => setRevokedInfo(null)}>OK</button>
          </div>
        )}

        <form onSubmit={async (e) => { e.preventDefault(); await doLogin(false); }}>
          <div className="login-field">
            <label htmlFor="username">{t('login.username')}</label>
            <input id="username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} required />
          </div>
          <div className="login-field">
            <label htmlFor="password">{t('login.password')}</label>
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="login-btn" disabled={isSubmitting}>
            {isSubmitting ? '...' : t('login.submit')}
          </button>
        </form>

        <div className="login-footer">
          <Link to="/">{t('login.back_home')}</Link>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
